import { DropdownSelectionProps } from '@shared/components/DropdownButton/DropdownSelection';
import Popup from '@shared/components/Popup/Popup';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowRight } from 'assets/icons/ArrowRight.svg';
import { ReactComponent as Plus } from 'assets/icons/Plus.svg';
import { ReactComponent as UserPlus } from 'assets/icons/UserPlus.svg';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import ModalFooter from 'components/ModalFooter';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import { calculatePermissions } from 'hooks/useWorkspacePermissions/utils';
import OptionsDropdown from 'modules/MediaDevicesSettings/OptionsDropdown';
import { getDefaultRoleTranslation } from 'modules/Workspaces/hooks/useCreateWorkspaceRole/utils';
import useWorkspaceRoles from 'modules/Workspaces/WorkspacesProvider/useWorkspaceRoles';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { getUserPowerLevel } from 'utils/matrixUtils';
import getAssignableWorkspaceRoles, {
  getDefaultAssignableWorkspaceRole,
} from 'utils/permissions/getAssignableWorkspaceRoles';

import useWorkspaceInvite from './useWorkspaceInvite';
import InvitePeopleInput from './WorkspaceInviteForm/InvitePeopleInput';
import useWorkspaceInviteForm from './WorkspaceInviteForm/useWorkspaceInviteForm';
import useCreateWorkspaceDataContext from '../CreateWorkspaceDataProvider/useCreateWorkspaceDataContext';
import { CreateWorkspaceMode, CreateWorkspaceStepsEnum } from '../types';
import WorkspaceFormField from '../WorkspaceCreateStep/WorkspaceFormField';

const WorkspaceInviteStep = () => {
  const { t } = useTranslation();
  const pushNoPermissionToast = useNoPermissionToast();
  const { matrixClient, hasReachedSyncReadyState } = useMatrixContext();
  const { setCurrentStep, removeModal, setWasInviteStepSkipped, createdWorkspaceId, mode, setInvitees } =
    useCreateWorkspaceDataContext();
  const { setWorkspaceId } = useWorkspacesContext();
  const { roles: workspaceRoles } = useWorkspaceRoles(createdWorkspaceId);
  const form = useWorkspaceInviteForm();
  const { watch, setValue, formState } = form;
  const { inviteUsers, isLoading } = useWorkspaceInvite();
  const scopedPowerLevels = useScopedUserPowerLevels(createdWorkspaceId);

  const invitees = watch('invitees');
  const role = watch('role');
  const hasInvitees = invitees.length > 0;
  const hasErrors = Object.keys(formState.errors).length > 0;
  const isInviteOnlyMode = mode === CreateWorkspaceMode.INVITE_ONLY;

  const workspaceName = createdWorkspaceId && matrixClient.getRoom(createdWorkspaceId)?.name;

  const inviteWorkspacePermissions = useMemo(
    () => (createdWorkspaceId ? calculatePermissions(matrixClient, createdWorkspaceId) : null),
    [createdWorkspaceId, matrixClient]
  );
  const hasRolePermissions = inviteWorkspacePermissions?.adjustRoles ?? false;

  const currentUserPowerLevel = useMemo(() => {
    if (!createdWorkspaceId) return null;

    const userId = matrixClient.getUserId();
    const workspaceRoom = matrixClient.getRoom(createdWorkspaceId);
    if (!userId || !workspaceRoom) return null;

    return Math.max(scopedPowerLevels.workspace, getUserPowerLevel(workspaceRoom, userId));
  }, [createdWorkspaceId, matrixClient, scopedPowerLevels.workspace]);

  const availableRoles = useMemo(() => {
    if (currentUserPowerLevel === null) return [];

    return getAssignableWorkspaceRoles(workspaceRoles, currentUserPowerLevel);
  }, [workspaceRoles, currentUserPowerLevel]);

  const hasValidAssignableRole = availableRoles.some(({ id }) => id === role);

  useEffect(() => {
    //NOTE: User shouldn't ever be here without createdWorkspaceId, navigate back to create step if that would happen
    if (!createdWorkspaceId) setCurrentStep(CreateWorkspaceStepsEnum.CREATE);
  }, [createdWorkspaceId, setCurrentStep]);

  useEffect(() => {
    if (availableRoles.length === 0) {
      // Only clear after PL is known; avoid wiping the form default while roles/PL are still loading.
      if (currentUserPowerLevel !== null && role != null) {
        setValue('role', null, { shouldValidate: true });
      }
      return;
    }

    const isSelectedRoleAvailable = !!role && availableRoles.some(({ id }) => id === role);
    const nextRoleId = isSelectedRoleAvailable ? role : getDefaultAssignableWorkspaceRole(availableRoles)?.id ?? null;

    if (nextRoleId !== role) {
      setValue('role', nextRoleId, { shouldValidate: true });
    }
  }, [availableRoles, currentUserPowerLevel, role, setValue]);

  const isInviteFormReady = hasReachedSyncReadyState && currentUserPowerLevel !== null && workspaceRoles.length > 0;

  const commitWorkspaceSelection = useCallback(() => {
    if (createdWorkspaceId) setWorkspaceId(createdWorkspaceId);
  }, [createdWorkspaceId, setWorkspaceId]);

  if (!createdWorkspaceId) return null;

  const resetInvitees = () => setInvitees([]);

  const handleSubmit = async () => {
    if (!createdWorkspaceId) return;

    const permissions = calculatePermissions(matrixClient, createdWorkspaceId);
    if (!permissions.invitePeople) return pushNoPermissionToast();

    const selectedRole = hasRolePermissions ? availableRoles.find(({ id }) => id === role) : null;

    if (hasRolePermissions && !selectedRole) {
      return pushNoPermissionToast();
    }

    await inviteUsers(createdWorkspaceId, invitees, selectedRole?.id ?? null);
    resetInvitees();
    if (isInviteOnlyMode) {
      commitWorkspaceSelection();
      return removeModal();
    }

    commitWorkspaceSelection();
    setWasInviteStepSkipped(false);
    setCurrentStep(CreateWorkspaceStepsEnum.SUCCESS);
  };

  const handleSkip = () => {
    resetInvitees();
    commitWorkspaceSelection();
    setWasInviteStepSkipped(true);
    setCurrentStep(CreateWorkspaceStepsEnum.SUCCESS);
  };

  const roleDropdownOptions: DropdownSelectionProps[] = [
    ...availableRoles.map(({ id, name, defaultRoleType }, index) => {
      const isLast = index === availableRoles.length - 1;
      const isSelected = role === id;
      const title = getDefaultRoleTranslation(defaultRoleType, name) || name;

      return {
        title,
        isSelected,
        onClick: () => setValue('role', id, { shouldValidate: true }),
        hasDivider: isLast && hasRolePermissions,
        selectionClassName: isLast ? 'mb-1' : '',
      };
    }),
    ...(hasRolePermissions
      ? [
          {
            title: t('workspaces.create_role'),
            icon: <Plus className="h-5 w-5 stroke-black mr-2" />,
            onClick: () => {
              setInvitees(invitees);
              commitWorkspaceSelection();
              return setCurrentStep(CreateWorkspaceStepsEnum.CREATE_ROLES);
            },
          },
        ]
      : []),
  ];

  const canSubmitInvite = hasRolePermissions ? hasValidAssignableRole : true;

  const handleCancelClick = isInviteOnlyMode ? removeModal : handleSkip;
  const cancelLabel = isInviteOnlyMode ? t('cancel') : t('skip_for_now');

  return (
    <FormProvider {...form}>
      <Popup isVisible wrapperId="create-workspace-modal">
        <Popup.FullScreenContainer className="md:w-[616px]">
          <div className="flex flex-col grow overflow-y-auto p-4" data-testid="invite-step-modal">
            <div className="flex flex-col items-center justify-center w-full py-6 text-center">
              <GradientIconWithShadow icon={UserPlus} iconClassName="!stroke-1.5" />
              {workspaceName && (
                <h1 className="text-2xl font-medium mb-2 mt-8 text-size-lg">
                  {t('workspaces.invite', { name: workspaceName })}
                </h1>
              )}
              <p className="max-w-[517px]">{t('workspaces.invite_subtitle')}</p>
            </div>
            <WorkspaceFormField label={t('workspaces.invite_people')}>
              <InvitePeopleInput />
            </WorkspaceFormField>
            {hasRolePermissions && (
              <WorkspaceFormField label={t('workspaces.invite_as')}>
                <OptionsDropdown
                  options={roleDropdownOptions}
                  selectClassName="h-12"
                  disabledTitle={t('workspaces.no_assignable_roles')}
                />
              </WorkspaceFormField>
            )}
          </div>
          <ModalFooter
            onCloseClick={handleCancelClick}
            onSubmit={handleSubmit}
            successButtonText={t('workspaces.send_invite')}
            successButtonIcon={ArrowRight}
            successButtonIconPosition="after"
            cancelText={cancelLabel}
            successDisabled={hasErrors || !hasInvitees || isLoading || !isInviteFormReady || !canSubmitInvite}
          />
        </Popup.FullScreenContainer>
      </Popup>
    </FormProvider>
  );
};

export default WorkspaceInviteStep;
